import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar, CheckCircle, Sparkles, Gift } from 'lucide-react';
import api, { formatApiErrorDetail } from '../utils/api';
import { BOOKING } from '../constants/testIds';
import { useLang } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { isStaffConnectedToService } from '../utils/serviceAvailability';

const nextDateValue = (offset) => {
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  date.setDate(date.getDate() + offset);
  return date.toLocaleDateString('en-CA');
};

const DateAvailabilityPicker = ({ staffId, duration, value, onChange, inputRef }) => {
  const [state, setState] = useState({ loading: true, dates: [] });

  useEffect(() => {
    let cancelled = false;
    const loadDates = async () => {
      setState({ loading: true, dates: [] });
      const dates = await Promise.all(Array.from({ length: 14 }, async (_, offset) => {
        const date = nextDateValue(offset);
        try {
          const { data } = await api.get('/availability/slots', { params: { staff_id: staffId, date, duration } });
          return (data.slots || []).some((slot) => slot.available) ? date : null;
        } catch { return null; }
      }));
      if (!cancelled) setState({ loading: false, dates: dates.filter(Boolean) });
    };
    loadDates();
    return () => { cancelled = true; };
  }, [staffId, duration]);

  const label = (date) => new Intl.DateTimeFormat('en-BE', { weekday: 'short', day: 'numeric', month: 'short' }).format(new Date(`${date}T12:00:00`));
  if (state.loading) return <p className="text-sm text-[#0d3b25]/60 py-3" data-testid="booking-date-loading">Checking available dates…</p>;
  if (!state.dates.length) return <p className="text-sm text-red-700 py-3" role="alert" data-testid="booking-date-unavailable">No available dates were found in the next 14 days.</p>;
  return <select ref={inputRef} value={value} onChange={(event) => onChange(event.target.value)} className="w-full p-3 bg-white text-[#0d3b25] border border-[#c5a059]/40 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#c5a059]" data-testid={BOOKING.dateInput} aria-label="Select available date"><option value="">Select an available date</option>{state.dates.map((date) => <option key={date} value={date}>{label(date)}</option>)}</select>;
};

// Live-availability picker — polls /api/availability/slots for the selected staff/date/duration
const TimeSlotPicker = ({ staffId, date, duration, value, onChange, autoFocus }) => {
  const [state, setState] = useState({ loading: true, closed: false, reason: '', slots: [] });

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    api.get('/availability/slots', { params: { staff_id: staffId, date, duration } })
      .then(({ data }) => {
        if (cancelled) return;
        setState({ loading: false, closed: !!data.closed, reason: data.reason || '', slots: data.slots || [] });
      })
      .catch(() => { if (!cancelled) setState({ loading: false, closed: true, reason: 'load-failed', slots: [] }); });
    return () => { cancelled = true; };
  }, [staffId, date, duration]);

  useEffect(() => {
    if (!autoFocus || state.loading || state.closed || value) return undefined;
    const focusTimer = setTimeout(() => {
      document.querySelector('button[data-testid^="timeslot-"]:not(:disabled)')?.focus({ preventScroll: true });
    }, 100);
    return () => clearTimeout(focusTimer);
  }, [autoFocus, state.loading, state.closed, value]);

  if (state.loading) return <div className="text-sm text-[#0d3b25]/60 py-4" data-testid="picker-loading">Loading availability…</div>;
  if (state.closed) return (
    <div className="text-sm bg-red-50 border border-red-300 text-red-700 rounded-sm p-4" data-testid="picker-closed">
      This date is not available {state.reason ? `(${state.reason})` : ''}.
    </div>
  );

  const availableSlots = state.slots.filter((slot) => slot.available);
  const availableCount = availableSlots.length;
  const grouped = availableSlots.reduce((acc, s) => {
    const hour = s.time.slice(0, 2);
    (acc[hour] = acc[hour] || []).push(s);
    return acc;
  }, {});

  return (
    <div>
      <p className="text-xs text-[#0d3b25]/60 mb-3" data-testid="picker-summary">
        <strong className="text-[#c5a059]">{availableCount}</strong> time slot(s) available • duration: {duration} min
      </p>
      {availableCount === 0 && (
        <p className="text-sm text-red-600 mb-3">No available times on this day. Please choose another date.</p>
      )}
      <div className="space-y-3" data-testid="time-slot-picker">
        {Object.keys(grouped).sort().map((hour) => (
          <div key={hour}>
            <div className="text-[10px] uppercase tracking-widest text-[#0d3b25]/50 mb-1">{hour}:00</div>
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
              {grouped[hour].map((s) => {
                const active = value === s.time;
                return (
                  <button
                    type="button"
                    key={s.time}
                    onClick={() => onChange(s.time)}
                    data-testid={`timeslot-${s.time.replace(':', '')}`}
                    aria-pressed={active}
                    className={`text-sm py-2 rounded-sm border transition-all ${
                      active
                        ? 'bg-[#c5a059] text-[#04140e] border-[#c5a059] font-semibold shadow-md'
                        : 'bg-white text-[#0d3b25] border-[#c5a059]/40 hover:border-[#c5a059] hover:bg-[#c5a059]/10'
                    }`}
                    title="Available"
                  >
                    {s.time}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Booking = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, localize, lang } = useLang();
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [promo, setPromo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const errorRef = useRef(null);
  const staffRef = useRef(null);
  const serviceRef = useRef(null);
  const dateRef = useRef(null);
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const phoneRef = useRef(null);
  const consentRef = useRef(null);

  // Bring a section near the top (accounting for the fixed navbar) so the next action is in view
  // Center + focus a specific field the customer must fix
  const focusField = (ref) => {
    if (!ref?.current) return;
    ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (typeof ref.current.focus === 'function') {
      setTimeout(() => { try { ref.current.focus({ preventScroll: true }); } catch { ref.current.focus(); } }, 350);
    }
  };

  const preselectedServiceId = searchParams.get('service');
  const preselectedPromoId = searchParams.get('promo');
  const preselectedStaffId = searchParams.get('staff');
  const preselectedDate = searchParams.get('date');
  const preselectedTime = searchParams.get('time');
  const isAdmin = user && user.role === 'admin';

  const [formData, setFormData] = useState({
    service_ids: preselectedServiceId ? [preselectedServiceId] : [],
    staff_id: preselectedStaffId || '',
    date: preselectedDate || '',
    time: preselectedTime || '',
    client_name: '',
    client_email: '',
    client_phone: '',
    privacy_consent: false,
    marketing_consent: false,
    register_account: false,
    notes: '',
    payment_method: 'salon',
    language: lang,
    promo_id: preselectedPromoId || null,
    voucher_code: '',
  });

  const [voucherApplied, setVoucherApplied] = useState(null);
  const [voucherError, setVoucherError] = useState('');
  const [voucherBusy, setVoucherBusy] = useState(false);
  const [publicSettings, setPublicSettings] = useState({ payment_methods_enabled: { salon: true, bancontact: true, card: true }, deposit_percent: 20 });

  useEffect(() => {
    api.get('/settings/public').then(({ data }) => {
      const pm = data.payment_methods_enabled || { salon: true, bancontact: true, card: true };
      setPublicSettings({ payment_methods_enabled: pm, deposit_percent: data.deposit_percent ?? 20 });
      setFormData((prev) => (pm[prev.payment_method] === false
        ? { ...prev, payment_method: (['salon', 'bancontact', 'card'].find((k) => pm[k]) || 'salon') }
        : prev));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [servicesRes, staffRes] = await Promise.all([api.get('/services'), api.get('/staff')]);
        setServices(servicesRes.data);
        setStaff(staffRes.data);

        if (preselectedPromoId) {
          const { data: promos } = await api.get('/promotions');
          const p = promos.find((x) => x.id === preselectedPromoId);
          if (p) {
            setPromo(p);
            setFormData((prev) => ({
              ...prev,
              service_ids: p.service_ids || [],
              staff_id: p.staff_id || prev.staff_id,
              promo_id: p.id,
            }));
          }
        } else if (preselectedServiceId) {
          const svc = servicesRes.data.find((s) => s.id === preselectedServiceId);
          if (svc && Array.isArray(svc.staff_ids) && svc.staff_ids.length === 1) {
            setFormData((prev) => ({ ...prev, staff_id: svc.staff_ids[0] }));
          }
        }
      } catch (err) {
        console.error('Fetch failed', err);
        setError('Could not load data. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user && user.email) {
      setFormData((prev) => ({
        ...prev,
        client_email: prev.client_email || user.email,
        client_name: prev.client_name || user.name || '',
        client_phone: prev.client_phone || user.phone || '',
      }));
    }
  }, [user]);

  const handleServiceToggle = (serviceId) => {
    setFormData((prev) => ({
      ...prev,
      service_ids: prev.service_ids.includes(serviceId)
        ? prev.service_ids.filter((id) => id !== serviceId)
        : [...prev.service_ids, serviceId],
    }));
  };

  const selectedServices = useMemo(
    () => services.filter((s) => formData.service_ids.includes(s.id)),
    [services, formData.service_ids],
  );
  const subtotal = promo
    ? promo.offer_price
    : selectedServices.reduce((sum, s) => sum + s.price, 0);
  const voucherDiscount = voucherApplied ? voucherApplied.discount : 0;
  const totalPrice = Math.max(0, subtotal - voucherDiscount);
  const totalDuration = selectedServices.reduce(
    (sum, s) => sum + s.duration + (s.buffer_time || 0) + (s.processing_time || 0),
    0,
  );

  const applyVoucher = async () => {
    if (!formData.voucher_code) return;
    setVoucherBusy(true);
    setVoucherError('');
    try {
      const { data } = await api.post('/vouchers/apply', {
        code: formData.voucher_code.trim().toUpperCase(),
        subtotal: subtotal,
        email: formData.client_email || null,
      });
      setVoucherApplied({ code: data.code, discount: data.discount, total: data.total });
    } catch (err) {
      setVoucherError(formatApiErrorDetail(err.response?.data?.detail) || 'Invalid code');
      setVoucherApplied(null);
    } finally { setVoucherBusy(false); }
  };
  const removeVoucher = () => {
    setVoucherApplied(null);
    setFormData({ ...formData, voucher_code: '' });
  };

  const eligibleStaff = useMemo(() => {
    if (formData.service_ids.length === 0) return staff.filter((member) => member.active !== false);
    return staff.filter((member) => selectedServices.every((service) => isStaffConnectedToService(service, member)));
  }, [staff, formData.service_ids, selectedServices]);

  const noConnectedStaff = formData.service_ids.length > 0 && selectedServices.length > 0 && eligibleStaff.length === 0;

  const skipServiceStep = !!promo || !!preselectedServiceId;
  const skipStaffStep = !!promo || (formData.staff_id && eligibleStaff.length === 1);

  useEffect(() => {
    if (promo || formData.staff_id || eligibleStaff.length !== 1) return;
    setFormData((previous) => ({ ...previous, staff_id: eligibleStaff[0].id }));
  }, [promo, formData.staff_id, eligibleStaff]);

  // On entry and after every completed step, keep the next required action visible and focused.
  useEffect(() => {
    if (loading || services.length === 0) return undefined;
    let actionRef = null;
    if (formData.service_ids.length === 0) actionRef = serviceRef;
    else if (!formData.staff_id && eligibleStaff.length !== 1 && !skipStaffStep) actionRef = staffRef;
    else if (!formData.date) actionRef = dateRef;
    else if (formData.time && !formData.client_name) actionRef = nameRef;
    if (!actionRef?.current) return undefined;
    const focusTimer = setTimeout(() => focusField(actionRef), 200);
    return () => clearTimeout(focusTimer);
  }, [loading, services.length, formData.service_ids.length, formData.staff_id, formData.date, formData.time, formData.client_name, eligibleStaff.length, skipStaffStep]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(formData.client_email)) {
      setError(t.booking.invalidEmail);
      focusField(emailRef);
      return;
    }
    // Phone must contain exactly 10 digits (Belgian format, e.g. 0470 12 34 56)
    let digits = (formData.client_phone || '').replace(/\D/g, '');
    if (digits.startsWith('0032')) digits = '0' + digits.slice(4);
    else if (digits.startsWith('32') && digits.length === 11) digits = '0' + digits.slice(2);
    if (digits.length !== 10) {
      setError('Please enter a valid phone number with 10 digits (e.g. 0470 12 34 56).');
      focusField(phoneRef);
      return;
    }
    if (!formData.privacy_consent) {
      setError(t.booking.requiredPrivacy);
      focusField(consentRef);
      return;
    }
    if (noConnectedStaff) {
      setError('This service is not currently connected to an available specialist. Please select another service.');
      return;
    }
    if (!formData.staff_id) {
      setError(t.booking.selectStaff);
      focusField(staffRef);
      return;
    }

    setLoading(true);
    try {
      const payload = { ...formData, voucher_code: voucherApplied ? voucherApplied.code : undefined };
      const { data } = await api.post('/bookings', payload);
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setSuccess(true);
      setTimeout(() => navigate(isAdmin ? '/admin' : '/account'), 3000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Booking failed');
      setLoading(false);
    }
  };

  if (loading && services.length === 0) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center bg-[#faf6ec]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c5a059]"></div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center bg-[#faf6ec]">
        <div className="max-w-md w-full px-6 text-center">
          <div className="bg-white border border-[#c5a059]/40 rounded-sm p-12 shadow-lg">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h2 className="text-2xl font-serif font-bold text-[#0d3b25] mb-4">{t.booking.confirmed}</h2>
            <p className="text-[#0d3b25]/70 mb-6">{t.booking.confirmedText}</p>
            <p className="text-sm text-[#0d3b25]/50">{t.booking.redirecting}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 bg-[#faf6ec]">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-serif font-bold text-[#0d3b25] mb-2">{t.booking.pageTitle}</h1>
          <p className="text-[#0d3b25]/60">{t.booking.subtitle}</p>
          {isAdmin && (
            <div className="mt-3 bg-[#c5a059] text-[#04140e] text-xs rounded-full px-4 py-1 inline-block font-semibold">
              ADMIN MODE — Booking on behalf of the salon
            </div>
          )}
        </div>

        {promo && (
          <div className="bg-[#04140e] text-[#f5e9ce] border border-[#c5a059] rounded-sm p-6 mb-6 flex items-start gap-4" data-testid="booking-promo-banner">
            <Sparkles className="w-6 h-6 text-[#c5a059] flex-shrink-0 mt-1" />
            <div>
              <p className="text-[10px] tracking-widest text-[#c5a059] mb-1">{t.offers.exclusive}</p>
              <h3 className="font-serif text-2xl font-bold mb-1">{localize(promo, 'title')}</h3>
              {localize(promo, 'description') && (
                <p className="text-sm text-[#f5e9ce]/80 mb-2" data-testid="booking-promo-description">{localize(promo, 'description')}</p>
              )}
              <p className="text-sm text-[#f5e9ce]/70">
                {(promo.services || []).map((s) => localize(s, 'name')).join(' • ')}
              </p>
              <div className="mt-3 flex items-baseline space-x-3">
                <span className="text-[#f5e9ce]/50 line-through">€{promo.total_price?.toFixed(0)}</span>
                <span className="text-[#c5a059] font-serif font-bold text-3xl">€{promo.offer_price?.toFixed(0)}</span>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white border border-[#c5a059]/30 rounded-sm p-8 shadow-sm">
          <form onSubmit={handleSubmit} className="space-y-8">
            {skipServiceStep && !promo && selectedServices.length > 0 && (
              <div className="bg-[#faf6ec] border border-[#c5a059]/40 rounded-sm p-5" data-testid="booking-selected-service-summary">
                <p className="text-[10px] tracking-widest text-[#c5a059] uppercase mb-1">Selected service</p>
                {selectedServices.map((svc) => (
                  <div key={svc.id} className="flex items-center justify-between">
                    <div>
                      <h3 className="font-serif text-lg text-[#0d3b25]">{localize(svc, 'name')}</h3>
                      <p className="text-xs text-[#0d3b25]/50">{svc.duration} min</p>
                    </div>
                    <span className="font-semibold text-[#c5a059]">€{svc.price.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}

            {!skipServiceStep && (
              <div>
                <h2 className="text-xl font-serif font-semibold text-[#0d3b25] mb-4">{t.booking.step1}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid={BOOKING.serviceSelect}>
                  {services.map((service) => (
                    <button
                      type="button"
                      key={service.id}
                      ref={service.id === services[0]?.id ? serviceRef : null}
                      onClick={() => handleServiceToggle(service.id)}
                      className={`p-4 border rounded-sm cursor-pointer transition-all ${
                        formData.service_ids.includes(service.id)
                          ? 'border-[#c5a059] bg-[#c5a059]/10'
                          : 'border-[#c5a059]/30 bg-white hover:border-[#c5a059]/60'
                      }`}
                      data-testid={`booking-service-${service.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-[#0d3b25]">{localize(service, 'name')}</h3>
                          <p className="text-xs text-[#0d3b25]/50 mt-1">{service.duration} min</p>
                        </div>
                        <span className="font-semibold text-[#c5a059]">€{service.price.toFixed(2)}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {noConnectedStaff && <div className="bg-red-50 border border-red-300 text-red-700 rounded-sm p-4" role="alert" data-testid="booking-service-unavailable">This service is not currently connected to an available specialist. Please choose another service.</div>}

            {!noConnectedStaff && !skipStaffStep && formData.service_ids.length > 0 && (
              <div>
                <h2 className="text-xl font-serif font-semibold text-[#0d3b25] mb-4">{t.booking.step2}</h2>
                <select
                  ref={staffRef}
                  required
                  value={formData.staff_id}
                  onChange={(e) => setFormData({ ...formData, staff_id: e.target.value })}
                  className="w-full p-3 bg-white text-[#0d3b25] border border-[#c5a059]/40 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                  data-testid={BOOKING.staffSelect}
                >
                  <option value="" label={String(t.booking.selectStaff)} />
                  {eligibleStaff.map((s) => (
                    <option key={s.id} value={s.id} label={`${String(s.name || '')} — ${String(s.role || '')}`} />
                  ))}
                </select>
              </div>
            )}

            {!noConnectedStaff && formData.service_ids.length > 0 && formData.staff_id && (
              <div>
                <h2 className="text-xl font-serif font-semibold text-[#0d3b25] mb-4">{t.booking.step3}</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="md:col-span-1">
                    <label className="text-sm font-medium text-[#0d3b25] mb-2 block">{t.booking.date}</label>
                    <DateAvailabilityPicker staffId={formData.staff_id} duration={totalDuration || 30} value={formData.date} onChange={(date) => setFormData({ ...formData, date, time: '' })} inputRef={dateRef} />
                  </div>
                </div>
                {formData.date && (
                  <TimeSlotPicker
                    staffId={formData.staff_id}
                    date={formData.date}
                    duration={totalDuration || 30}
                    value={formData.time}
                    onChange={(tm) => setFormData({ ...formData, time: tm })}
                    autoFocus={!formData.time}
                  />
                )}
              </div>
            )}

            {formData.date && formData.time && (
              <div>
                <h2 className="text-xl font-serif font-semibold text-[#0d3b25] mb-4">{t.booking.step4}</h2>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-[#0d3b25] mb-2 block">{t.booking.name}</label>
                    <Input ref={nameRef} required value={formData.client_name} onChange={(e) => setFormData({ ...formData, client_name: e.target.value })} className="bg-white text-[#0d3b25] border-[#c5a059]/40 placeholder:text-[#0d3b25]/40 focus-visible:ring-[#c5a059]" data-testid="booking-name-input" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#0d3b25] mb-2 block">{t.booking.email}</label>
                    <Input ref={emailRef} type="email" required value={formData.client_email} onChange={(e) => setFormData({ ...formData, client_email: e.target.value })} className="bg-white text-[#0d3b25] border-[#c5a059]/40 placeholder:text-[#0d3b25]/40 focus-visible:ring-[#c5a059]" data-testid="booking-email-input" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#0d3b25] mb-2 block">{t.booking.phone}</label>
                    <Input ref={phoneRef} type="tel" required value={formData.client_phone} onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })} placeholder="0470 12 34 56" className="bg-white text-[#0d3b25] border-[#c5a059]/40 placeholder:text-[#0d3b25]/40 focus-visible:ring-[#c5a059]" data-testid="booking-phone-input" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#0d3b25] mb-2 block">{t.booking.notes}</label>
                    <Input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder={t.booking.notesPlaceholder} className="bg-white text-[#0d3b25] border-[#c5a059]/40 placeholder:text-[#0d3b25]/40 focus-visible:ring-[#c5a059]" data-testid="booking-notes-input" />
                  </div>
                </div>

                {!user && (
                  <label
                    htmlFor="register_account"
                    className="mt-6 flex items-start gap-4 p-5 border-2 border-dashed border-[#c5a059] bg-gradient-to-r from-[#c5a059]/10 to-[#0d3b25]/5 rounded-sm cursor-pointer hover:from-[#c5a059]/20 transition-colors"
                    data-testid="booking-register-checkbox-wrapper"
                  >
                    <Checkbox
                      id="register_account"
                      checked={formData.register_account}
                      onCheckedChange={(v) => setFormData({ ...formData, register_account: !!v })}
                      className="mt-1"
                      data-testid="booking-register-checkbox"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Gift className="w-5 h-5 text-[#c5a059] animate-pulse" />
                        <span className="font-serif font-bold text-[#0d3b25] text-lg">
                          Registreer &amp; krijg 5% korting
                        </span>
                        <span className="bg-[#c5a059] text-[#04140e] text-xs font-bold px-3 py-0.5 rounded-full">-5%</span>
                      </div>
                      <p className="text-xs text-[#0d3b25]/60">
                        Maak een account aan met dit email en ontvang een unieke kortingscode van 5% voor uw volgende afspraak (6 maanden geldig).
                      </p>
                    </div>
                  </label>
                )}
              </div>
            )}

            {formData.client_email && (
              <div>
                <h2 className="text-xl font-serif font-semibold text-[#0d3b25] mb-4">{t.booking.step5}</h2>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3" ref={consentRef}>
                    <Checkbox
                      id="privacy"
                      checked={formData.privacy_consent}
                      onCheckedChange={(checked) => setFormData({ ...formData, privacy_consent: checked })}
                      data-testid="booking-privacy-consent"
                    />
                    <label htmlFor="privacy" className="text-sm text-[#0d3b25]/70 leading-relaxed">
                      {t.booking.privacyText}{' '}
                      <a href="/privacy" target="_blank" className="text-[#c5a059] underline">{t.booking.privacyPolicy}</a>{' '}
                      {t.booking.andThe}{' '}
                      <a href="/terms" target="_blank" className="text-[#c5a059] underline">{t.booking.termsConditions}</a>. *
                    </label>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="marketing"
                      checked={formData.marketing_consent}
                      onCheckedChange={(checked) => setFormData({ ...formData, marketing_consent: checked })}
                      data-testid="booking-marketing-consent"
                    />
                    <label htmlFor="marketing" className="text-sm text-[#0d3b25]/70 leading-relaxed">
                      {t.booking.marketingText}
                    </label>
                  </div>
                </div>
              </div>
            )}

            {formData.privacy_consent && (
              <div className="border-t border-[#c5a059]/25 pt-6">
                <h3 className="font-serif font-semibold text-[#0d3b25] mb-2">{t.booking.paymentMethod || 'Payment Method'}</h3>
                <p className="text-xs text-[#0d3b25]/60 mb-4" data-testid="deposit-note">
                  A {publicSettings.deposit_percent}% deposit is required to confirm your booking. Pay now and receive a discount voucher in the salon.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    { key: 'salon', label: t.booking.payAtSalon || 'Pay at the salon', hint: 'Cash / Bancontact on-site' },
                    { key: 'bancontact', label: 'Bancontact / Payconiq', hint: `${publicSettings.deposit_percent}% deposit online` },
                    { key: 'card', label: 'Visa / Mastercard', hint: `${publicSettings.deposit_percent}% deposit online` },
                  ].filter((opt) => publicSettings.payment_methods_enabled?.[opt.key] !== false).map((opt) => (
                    <label
                      key={opt.key}
                      className={`p-4 border rounded-sm cursor-pointer transition-colors flex items-start gap-3 ${
                        formData.payment_method === opt.key ? 'border-[#c5a059] bg-[#c5a059]/10' : 'border-[#c5a059]/25 bg-white hover:border-[#c5a059]/60'
                      }`}
                      data-testid={`booking-payment-${opt.key}`}
                    >
                      <input
                        type="radio"
                        name="payment_method"
                        value={opt.key}
                        checked={formData.payment_method === opt.key}
                        onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                        className="mt-1 accent-[#c5a059]"
                      />
                      <div>
                        <p className="font-medium text-[#0d3b25] text-sm">{opt.label}</p>
                        <p className="text-[10px] text-[#0d3b25]/50 mt-0.5">{opt.hint}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {formData.privacy_consent && (
              <div className="border-t border-[#c5a059]/25 pt-6">
                <div className="bg-[#faf6ec] border border-[#c5a059]/20 rounded-sm p-6 mb-6">
                  <h3 className="font-semibold text-[#0d3b25] mb-4">{t.booking.summary}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#0d3b25]/60">Services:</span>
                      <span className="font-medium text-[#0d3b25]">{selectedServices.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#0d3b25]/60">{t.booking.totalDuration}:</span>
                      <span className="font-medium text-[#0d3b25]">{totalDuration} min</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#0d3b25]/60">Subtotal:</span>
                      <span className="font-medium text-[#0d3b25]">€{subtotal.toFixed(2)}</span>
                    </div>
                    {voucherApplied && (
                      <div className="flex justify-between text-green-600">
                        <span>Voucher ({voucherApplied.code}):</span>
                        <span>−€{voucherDiscount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-semibold text-[#c5a059] border-t border-[#c5a059]/20 pt-2">
                      <span>{t.booking.totalPrice}:</span>
                      <span>€{totalPrice.toFixed(2)}</span>
                    </div>
                  </div>

                  {!voucherApplied ? (
                    <div className="mt-4 pt-4 border-t border-[#c5a059]/20">
                      <label className="text-xs uppercase tracking-wider text-[#0d3b25]/50 block mb-2">Voucher / gift card code</label>
                      <div className="flex gap-2">
                        <Input
                          value={formData.voucher_code}
                          onChange={(e) => setFormData({ ...formData, voucher_code: e.target.value.toUpperCase() })}
                          placeholder="GIFT-XXXXXXXX"
                          className="bg-white border-[#c5a059]/30 text-[#0d3b25] placeholder:text-[#0d3b25]/30"
                          data-testid="booking-voucher-input"
                        />
                        <Button
                          type="button"
                          onClick={applyVoucher}
                          disabled={voucherBusy || !formData.voucher_code}
                          className="bg-[#c5a059] text-[#04140e] hover:bg-[#d4b56e] font-semibold"
                          data-testid="booking-voucher-apply"
                        >
                          {voucherBusy ? '...' : 'Apply'}
                        </Button>
                      </div>
                      {voucherError && (
                        <p className="text-xs text-red-600 mt-2" data-testid="booking-voucher-error">{voucherError}</p>
                      )}
                    </div>
                  ) : (
                    <div className="mt-4 pt-4 border-t border-[#c5a059]/20 flex items-center justify-between">
                      <span className="text-sm text-green-600">Code {voucherApplied.code} applied</span>
                      <button type="button" onClick={removeVoucher} className="text-xs text-[#c5a059] hover:underline" data-testid="booking-voucher-remove">
                        Remove
                      </button>
                    </div>
                  )}
                </div>

                {error && (
                  <div
                    ref={errorRef}
                    className="bg-red-50 border-2 border-red-300 text-red-700 rounded-sm p-4 mb-4 flex items-start gap-3"
                    data-testid="booking-error"
                    role="alert"
                    aria-live="assertive"
                  >
                    <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="font-semibold">Booking failed</p>
                      <p className="text-sm">{error}</p>
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={
                    loading ||
                    formData.service_ids.length === 0 ||
                    !formData.staff_id ||
                    !formData.date ||
                    !formData.time ||
                    !formData.client_name?.trim() ||
                    !formData.client_email?.trim() ||
                    !formData.client_phone?.trim() ||
                    !formData.privacy_consent
                  }
                  className="w-full bg-[#c5a059] text-[#04140e] hover:bg-[#d4b56e] rounded-full font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid={BOOKING.submitBtn}
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  {loading ? t.booking.submitting : t.booking.submit}
                </Button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default Booking;
